import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { useNetwork } from "@/shared/context/NetworkContext";

const NetworkConnectionStatus: React.FC = () => {
    const { t } = useTranslation("common");
    const { isOnline } = useNetwork();
    const [wasOffline, setWasOffline] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (!isOnline) {
            setWasOffline(true);
            setIsVisible(true);
            setIsAnimating(true);
        } else if (wasOffline) {
            setIsVisible(true);
            setIsAnimating(true);
            // Auto-reload after connection is restored
            const timer = setTimeout(() => {
                handleContinue();
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            setIsAnimating(false);
            // Hide component after animation completes
            const timer = setTimeout(() => {
                setIsVisible(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [isOnline, wasOffline]);

    const handleReload = useCallback(() => {
        window.location.reload();
    }, []);

    const handleContinue = useCallback(() => {
        setWasOffline(false);
        setIsAnimating(false);
        // Delay reload to show the animation
        setTimeout(() => {
            window.location.reload();
        }, 800);
    }, []);

    // Don't render if not visible
    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-50 transition-all duration-700 ease-in-out">
            {/* Professional, clean overlay with grayscale effect - now blocks interactions when offline */}
            <div
                className={`absolute inset-0 backdrop-filter backdrop-grayscale backdrop-blur-sm bg-black/10 transition-all duration-1000 ${!isOnline
                        ? "opacity-100 pointer-events-auto"
                        : "opacity-0 pointer-events-none"
                    }`}
            />

            {/* Subtle color restoration effect */}
            {isOnline && wasOffline && (
                <div className="absolute inset-0 bg-gradient-to-t from-transparent to-white/5 transition-opacity duration-1000 pointer-events-none" />
            )}

            {/* Status indicator with clean, professional design */}
            <div
                className={`absolute bottom-6 right-6 transform transition-all duration-500 ${isAnimating ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
                    }`}
            >
                <div
                    className={`rounded-lg p-4 shadow-md transition-all duration-500 ${!isOnline
                            ? "bg-gray-800/95 text-white border-l-4 border-red-500"
                            : "bg-gray-800/95 text-white border-l-4 border-green-500"
                        }`}
                >
                    {!isOnline ? (
                        <div className="flex items-center space-x-3">
                            <WifiOff className="h-5 w-5 text-red-400" />
                            <div className="flex flex-col">
                                <span className="font-medium">{t("networkStatus.connectionLost")}</span>
                                <span className="text-xs text-gray-400">{t("networkStatus.checkConnection")}</span>
                            </div>
                            <Button
                                onClick={handleReload}
                                variant="ghost"
                                size="sm"
                                className="ml-2 hover:bg-gray-700/80 transition-all duration-300"
                                aria-label={t("networkStatus.reload")}
                            >
                                <RefreshCw className="h-4 w-4" />
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center space-x-3">
                            <Wifi className="h-5 w-5 text-green-400" />
                            <div className="flex flex-col">
                                <span className="font-medium">{t("networkStatus.connectionRestored")}</span>
                                <span className="text-xs text-gray-400">{t("networkStatus.reloading")}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NetworkConnectionStatus; 